/**
 * StockOverviewPage - Visão Geral da Gestão Rastreador
 *
 * Indicadores por tipo, por local (estoque, prestador, cliente), por situação,
 * por endereço e itens parados. Fonte única: `get_stock_overview_kpis`.
 * "Unidade" = kit montado ou rastreador/chip fora de kit.
 *
 * RELEASE: 2026-10-10 v1.2.0 - Atualiza o título visual para Gestão Rastreador.
 * RELEASE: 2026-10-09 v1.1.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 5 do Estoque).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlarmClock, Car, Cpu, CreditCard, MapPin, Package, Wrench } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import { ListPageLayout, ListPageHeader, ListPageKpis, ListPageContent, KpiCard, LIST_FILTERS_SELECT_TRIGGER_CLASS } from '@/components/layout/list-page';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { PREMIUM_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';
import { statusesFor, stockStatusLabel, stockStatusTone, type StockItemKind } from '@/hooks/useStockItems';
import { stockLocationTypeLabel } from '@/hooks/useStockRegistries';
import { useStockOverview } from '@/hooks/useStockMovements';
import { StockPlaceLabel } from '@/components/stock/StockPlaceLabel';

const KIND = { kit: 'Kit', tracker: 'Rastreador', chip: 'Chip' } as const;
const ROUTE: Record<StockItemKind, string> = { kit: '/tenant/stock/kits', tracker: '/tenant/stock/trackers', chip: '/tenant/stock/chips' };

/** Bloco com título padrão. */
function Block({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <Card className={`${PREMIUM_DASHBOARD_CARD} p-4 ${className ?? ''}`}>
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {children}
    </Card>
  );
}

export default function StockOverviewPage() {
  const navigate = useNavigate();
  const [staleDays, setStaleDays] = useState(60);
  const { data, isLoading } = useStockOverview(staleDays);

  return (
    <ListPageLayout>
      <ListPageHeader title="Visão Geral — Gestão Rastreador" />
      {isLoading || !data ? (
        <div className="space-y-4 p-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-64 w-full" /></div>
      ) : (
        <>
          <ListPageKpis columns={4}>
            <KpiCard color="blue" icon={Package} label="Kits montados" value={data.totals.kits} onClick={() => navigate(ROUTE.kit)} />
            <KpiCard color="purple" icon={Cpu} label="Rastreadores" value={data.totals.trackers} suffix={`${data.totals.free_trackers} livres`} onClick={() => navigate(ROUTE.tracker)} />
            <KpiCard color="cyan" icon={CreditCard} label="Chips" value={data.totals.chips} suffix={`${data.totals.free_chips} livres`} onClick={() => navigate(ROUTE.chip)} />
            <KpiCard color="orange" icon={AlarmClock} label="Parados" value={data.stale_count} suffix={`+${data.stale_days} dias`} />
          </ListPageKpis>
          <ListPageContent>
            <div className="grid gap-4 p-1 lg:grid-cols-3">
              <Block title="Por local (unidades)">
                <ul className="space-y-2 text-sm">
                  {[
                    { icon: MapPin, label: 'Em estoque', v: data.by_place.location },
                    { icon: Wrench, label: 'Com prestador', v: data.by_place.provider },
                    { icon: Car, label: 'Em clientes', v: data.by_place.customer },
                    { icon: Package, label: 'Sem local definido', v: data.by_place.none },
                  ].map((r) => (
                    <li key={r.label} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-muted-foreground"><r.icon className="h-4 w-4" />{r.label}</span>
                      <span className="tabular-nums font-semibold">{r.v}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">Unidade = kit montado ou rastreador/chip fora de kit.</p>
              </Block>

              <Block title="Por situação" className="lg:col-span-2">
                <div className="grid gap-4 sm:grid-cols-3">
                  {(['kit', 'tracker', 'chip'] as StockItemKind[]).map((k) => (
                    <div key={k}>
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">{KIND[k]}s</p>
                      <ul className="space-y-1 text-sm">
                        {statusesFor(k).map((s) => {
                          const n = data.by_status[k]?.[s.value] ?? 0;
                          return (
                            <li key={s.value} className={`flex items-center justify-between ${n === 0 ? 'opacity-40' : ''}`}>
                              <span className="flex items-center gap-1.5"><StatusDot tone={stockStatusTone(s.value)} />{s.label}</span>
                              <span className="tabular-nums">{n}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </Block>

              <Block title="Por endereço de estoque" className="lg:col-span-2">
                {data.by_location.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum endereço ativo.</p> : (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground">
                      <tr><th className="pb-2 text-left font-medium">Endereço</th><th className="pb-2 text-right font-medium">Kits</th>
                        <th className="pb-2 text-right font-medium">Rastreadores</th><th className="pb-2 text-right font-medium">Chips</th><th className="pb-2 text-right font-medium">Total</th></tr>
                    </thead>
                    <tbody>
                      {data.by_location.map((l) => (
                        <tr key={l.id} className="border-t">
                          <td className="py-1.5">{l.name} <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{stockLocationTypeLabel(l.type)}</Badge></td>
                          <td className="py-1.5 text-right tabular-nums">{l.kits}</td>
                          <td className="py-1.5 text-right tabular-nums">{l.trackers}</td>
                          <td className="py-1.5 text-right tabular-nums">{l.chips}</td>
                          <td className="py-1.5 text-right font-semibold tabular-nums">{l.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Block>

              <Block title="Com prestadores">
                {data.by_provider.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum item com prestador.</p> : (
                  <ul className="space-y-1.5 text-sm">
                    {data.by_provider.map((p) => (
                      <li key={p.id} className="flex items-center justify-between"><span className="truncate">{p.name}</span><span className="tabular-nums font-semibold">{p.count}</span></li>
                    ))}
                  </ul>
                )}
              </Block>

              <Block title="Itens parados" className="lg:col-span-3">
                <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                  Sem movimentação há mais de
                  <Select value={String(staleDays)} onValueChange={(v) => setStaleDays(Number(v))}>
                    <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} w-28`}><SelectValue /></SelectTrigger>
                    <SelectContent>{[30, 60, 90, 180].map((d) => <SelectItem key={d} value={String(d)}>{d} dias</SelectItem>)}</SelectContent>
                  </Select>
                  (em estoque ou com prestador)
                </div>
                {data.stale.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum item parado.</p> : (
                  <ul className="divide-y rounded-md border">
                    {data.stale.map((s) => (
                      <li key={s.kind + s.id} className="flex items-center gap-3 px-3 py-2">
                        <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{KIND[s.kind]}</Badge>
                        <span className="w-44 shrink-0 truncate tabular-nums text-sm font-medium">{s.label}</span>
                        <span className="w-40 shrink-0 text-xs text-muted-foreground">{stockStatusLabel(s.kind, s.status)}</span>
                        <div className="min-w-0 flex-1"><StockPlaceLabel place={s.place} showSince /></div>
                      </li>
                    ))}
                  </ul>
                )}
              </Block>
            </div>
          </ListPageContent>
        </>
      )}
    </ListPageLayout>
  );
}
